import { useCallback, useEffect, useState } from "react";
import { reviewTemplate } from "../../shared/review-templates";
import type { ApprovalDTO, ReviewPageDTO } from "../../shared/types";
import { useUser } from "../App";
import { AgentPictures } from "../components/AgentAvatar";
import { ReviewCard } from "../components/ReviewCard";
import { Icon, type IconName } from "../components/Icon";
import { Overlay } from "../components/Overlay";
import { Table } from "../components/Table";
import { Tabs, useTabParam } from "../components/Tabs";
import { Badge, ErrorText } from "../components/ui";
import { get } from "../lib/api";
import { canDecideRequest, releaseText } from "../lib/approval-text";
import { requestsPath, reviewPagePath } from "../lib/review-paths";
import { navigate } from "../lib/router";
import { OutreachReviewPage } from "./ReviewOutreach";

/** The mark each template's row carries: what kind of thing its page holds. */
const PAGE_ICON: Record<string, IconName> = { "outreach-message": "mail", "content-topic": "pages" };

/**
 * Request templates: the kinds of thing an agent files for a verdict before it uses it (D8, D14), one row each — what it
 * is for, the agents that hold it, and how many of its items wait for whoever is looking. A row opens the template's
 * own page, made for going through them one by one. Templates live in code; an agent is given one in its settings.
 */
export function RequestTemplates({ pages, admin = false }: { pages: ReviewPageDTO[] | null; admin?: boolean }) {
  if (!pages) return <div className="card h-32 animate-pulse" />;
  return (
    <Table
      rows={pages}
      rowKey={(p) => p.template}
      onRowClick={(p) => navigate(reviewPagePath(p.route, admin))}
      empty="No request templates."
      columns={[
        {
          header: "Template",
          primary: true,
          cell: (p) => {
            const t = reviewTemplate(p.template);
            return (
              <div className="min-w-0" title={t ? `${releaseText(t.release)}.` : undefined}>
                <div className="flex items-center gap-2 font-medium">
                  <Icon name={PAGE_ICON[p.template] ?? "pages"} className="size-4 shrink-0 text-faint" />
                  <span className="truncate">{t?.page ?? p.label}</span>
                </div>
                {t && <p className="mt-0.5 text-xs text-muted">{t.purpose}</p>}
              </div>
            );
          },
        },
        {
          header: "Agents that hold it",
          cell: (p) =>
            p.agents.length ? (
              <span className="flex min-w-0 items-center gap-2">
                <AgentPictures agents={p.agents} />
                <span className="truncate text-xs text-muted">{p.agents.map((a) => a.name).join(", ")}</span>
              </span>
            ) : (
              <span className="text-xs text-faint">No agent holds it yet</span>
            ),
        },
        {
          header: "For you",
          align: "right",
          cell: (p) => (p.waiting ? <Badge tone="amber">{p.waiting} waiting for you</Badge> : <span className="text-xs text-faint">Nothing waiting</span>),
        },
      ]}
    />
  );
}

/**
 * One template's own page, opened over the whole screen from its row under Request templates the way a request opens
 * from the list; closing it lands back on that tab. `outreach-message` has its own view (one message per screen, the mic on the
 * revision box); every other template is the plain list below.
 */
export function ReviewTemplatePage({ template, admin = false }: { template: string; admin?: boolean }) {
  const close = useCallback(() => navigate(requestsPath(admin)), [admin]);
  if (template === "outreach-message") return <OutreachReviewPage admin={admin} onClose={close} />;
  return <TemplateList key={template} template={template} onClose={close} />;
}

/**
 * `content-topic` until it gets its grid of topics: what is waiting for the person looking, with the same three
 * verdicts. An admin may page through everyone's (`?who=all`, its own parameter so the Requests page beneath keeps
 * its `?view=`) and still decides only what is addressed to them.
 */
function TemplateList({ template, onClose }: { template: string; onClose: () => void }) {
  const user = useUser();
  const known = reviewTemplate(template);
  const [items, setItems] = useState<ApprovalDTO[] | null>(null);
  const [who, setWho] = useTabParam(["mine", "all"] as const, "who");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    get<ApprovalDTO[]>(`/api/review/pending/${encodeURIComponent(template)}?view=${who}`).then(setItems, (e) => setError(e.message));
  }, [template, who]);
  useEffect(() => {
    load();
    const timer = setInterval(load, 15_000);
    return () => clearInterval(timer);
  }, [load]);

  return (
    <Overlay
      onClose={onClose}
      title={<span className="text-heading font-semibold tracking-tight">{known?.page ?? template}</span>}
      trailing={user.role === "admin" ? <Tabs label="Show" value={who} onChange={setWho} tabs={[{ key: "mine", label: "Mine" }, { key: "all", label: "All" }]} /> : undefined}
    >
      <div className="mx-auto w-full max-w-3xl space-y-4 px-4 py-6 md:px-6">
        <p className="text-sm text-muted">
          {known
            ? `What an agent has produced and needs a verdict on before it is used. Approve it, reject it, or send it back with what to change — the agent is told once nothing here is waiting. ${releaseText(known.release)}.`
            : "There is no request template with this name."}
        </p>
        <ErrorText error={error} />
        {!items ? (
          !error && <div className="card h-32 animate-pulse" />
        ) : items.length === 0 ? (
          <div className="card flex flex-col items-center gap-1 px-4 py-12 text-center">
            <span className="text-sm font-medium">Nothing waiting</span>
            <span className="text-xs text-muted">When an agent files one of these for a verdict, it shows up here and in the chat it was filed from.</span>
          </div>
        ) : (
          items.map((a) => <ReviewCard key={a.id} approval={a} canDecide={canDecideRequest(a, user)} onDecided={load} />)
        )}
      </div>
    </Overlay>
  );
}
